import { createRouter, createRoute, createRootRoute, redirect } from '@tanstack/react-router';
import { msalInstance } from '@/config/msalConfig';
import App from '../App';
import Login from '../pages/Login';
import Dashboard from '../pages/Dashboard';
import ContractList from '../pages/ContractList';
import ContractDetail from '../pages/ContractDetail';
import ContractForm from '../pages/ContractForm';
import VendorList from '../pages/VendorList';
import VendorDetail from '../pages/VendorDetail';
import VendorForm from '../pages/VendorForm';
import LicenseList from '../pages/LicenseList';
import LicenseDetail from '../pages/LicenseDetail';
import LicenseForm from '../pages/LicenseForm';
import Timeline from '../pages/Timeline';
import Renewals from '../pages/Renewals';
import Budgets from '../pages/Budgets';
import { RequireManage } from '@/components/RequireManage';
import {
  isBudgetFiscalYear,
  isContractStatus,
  isRenewalForecastGrouping,
  isRenewalForecastMonths,
  type ContractStatus,
  type RenewalForecastGrouping,
  type RenewalForecastMonths,
} from '../types';

/**
 * Check if user is authenticated (MSAL or local JWT)
 */
function isAuthenticated(): boolean {
  const hasMsalAccount = msalInstance.getAllAccounts().length > 0;
  const hasLocalToken = !!localStorage.getItem('auth_token');
  return hasMsalAccount || hasLocalToken;
}

/**
 * Root route - App shell layout (sidebar, header)
 * Protected: redirects to /login if not authenticated
 */
const rootRoute = createRootRoute({
  component: App,
  beforeLoad: ({ location }) => {
    if (location.pathname === '/login') return;
    if (!isAuthenticated()) {
      throw redirect({ to: '/login' });
    }
  },
});

// ─── Login ────────────────────────────────────────────────
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: Login,
});

// ─── Dashboard ────────────────────────────────────────────
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: Dashboard,
});

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard',
  component: Dashboard,
});

// ─── Contracts ────────────────────────────────────────────
/** `/contracts?status=<ContractStatus>` filters the list; an unknown value is ignored, never thrown. */
export interface ContractListSearch {
  status?: ContractStatus;
}

const contractsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/contracts',
  validateSearch: (search: Record<string, unknown>): ContractListSearch => ({
    status: isContractStatus(search.status) ? search.status : undefined,
  }),
  component: ContractList,
});

const contractNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/contracts/new',
  component: () => (
    <RequireManage area="contracts">
      <ContractForm />
    </RequireManage>
  ),
});

const contractDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/contracts/$id',
  component: ContractDetail,
});

const contractEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/contracts/$id/edit',
  component: () => (
    <RequireManage area="contracts">
      <ContractForm />
    </RequireManage>
  ),
});

// ─── Vendors ──────────────────────────────────────────────
const vendorsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/vendors',
  component: VendorList,
});

const vendorNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/vendors/new',
  component: () => (
    <RequireManage area="vendors">
      <VendorForm />
    </RequireManage>
  ),
});

const vendorDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/vendors/$id',
  component: VendorDetail,
});

const vendorEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/vendors/$id/edit',
  component: () => (
    <RequireManage area="vendors">
      <VendorForm />
    </RequireManage>
  ),
});

// ─── Licenses ─────────────────────────────────────────────
const licensesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/licenses',
  component: LicenseList,
});

const licenseNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/licenses/new',
  component: () => (
    <RequireManage area="licenses">
      <LicenseForm />
    </RequireManage>
  ),
});

const licenseDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/licenses/$id',
  component: LicenseDetail,
});

const licenseEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/licenses/$id/edit',
  component: () => (
    <RequireManage area="licenses">
      <LicenseForm />
    </RequireManage>
  ),
});

// ─── Timeline ────────────────────────────────────────────
const timelineRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/timeline',
  component: Timeline,
});

// ─── Renewals ────────────────────────────────────────────
/** `/renewals?groupBy=month|quarter&months=6|12|24|36`; unknown values are ignored, never thrown. */
export interface RenewalsSearch {
  groupBy?: RenewalForecastGrouping;
  months?: RenewalForecastMonths;
}

const renewalsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/renewals',
  validateSearch: (search: Record<string, unknown>): RenewalsSearch => {
    const months = Number(search.months);
    return {
      groupBy: isRenewalForecastGrouping(search.groupBy) ? search.groupBy : undefined,
      months: isRenewalForecastMonths(months) ? months : undefined,
    };
  },
  component: Renewals,
});

// ─── Budgets ─────────────────────────────────────────────
/** `/budgets?fiscalYear=2026`; a year outside 2000 to 2100 or not a number is ignored. */
export interface BudgetsSearch {
  fiscalYear?: number;
}

const budgetsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/budgets',
  validateSearch: (search: Record<string, unknown>): BudgetsSearch => {
    const year = Number(search.fiscalYear);
    return { fiscalYear: isBudgetFiscalYear(year) ? year : undefined };
  },
  component: Budgets,
});

// ─── Route Tree ───────────────────────────────────────────
const routeTree = rootRoute.addChildren([
  loginRoute,
  indexRoute,
  dashboardRoute,
  contractsRoute,
  contractNewRoute,
  contractDetailRoute,
  contractEditRoute,
  vendorsRoute,
  vendorNewRoute,
  vendorDetailRoute,
  vendorEditRoute,
  licensesRoute,
  licenseNewRoute,
  licenseDetailRoute,
  licenseEditRoute,
  timelineRoute,
  renewalsRoute,
  budgetsRoute,
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
